<template>
  <Card class="max-w-2xl border-red-200 dark:border-red-700">
    <template #header>
      <div class="flex items-center space-x-2">
        <ExclamationTriangleIcon class="w-5 h-5 text-red-500" />
        <h2 class="text-lg font-semibold text-red-900 dark:text-red-400">Danger Zone</h2>
      </div>
      <p class="text-sm text-red-600 dark:text-red-400 mt-1">
        Irreversible and destructive actions
      </p>
    </template>

    <div class="space-y-6">
      <!-- Account Deletion Warning -->
      <div
        class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4"
      >
        <div class="flex">
          <ExclamationTriangleIcon class="w-5 h-5 text-red-400 mt-0.5" />
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800 dark:text-red-300">Delete Account</h3>
            <div class="mt-2 text-sm text-red-700 dark:text-red-400">
              <p class="mb-2">
                Once you delete your account, there is no going back. This action cannot be undone.
              </p>
              <p class="font-medium">This will permanently:</p>
              <ul class="list-disc ml-5 mt-1 space-y-1">
                <li>Delete your user profile and account data</li>
                <li>Remove all your API keys and access tokens</li>
                <li>Cancel any active subscriptions</li>
                <li>Delete all associated data and preferences</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Deletion Form -->
      <form @submit.prevent="handleDeleteAccount" class="space-y-4">
        <div>
          <label
            for="confirmText"
            class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
          >
            Type <strong>DELETE</strong> to confirm account deletion
          </label>
          <Input
            id="confirmText"
            v-model="form.confirmText"
            type="text"
            placeholder="DELETE"
            :error="confirmTextError"
            :disabled="isLoading"
            required
            class="w-full"
          />
        </div>

        <div>
          <label
            for="password"
            class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
          >
            Enter your password to confirm
          </label>
          <Input
            id="password"
            v-model="form.password"
            type="password"
            placeholder="Enter your password"
            :error="passwordError"
            :disabled="isLoading"
            required
            class="w-full"
          />
        </div>

        <!-- Data Download Option -->
        <div
          class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4"
        >
          <div class="flex items-start">
            <InformationCircleIcon class="w-5 h-5 text-blue-400 mt-0.5" />
            <div class="ml-3">
              <h4 class="text-sm font-medium text-blue-800 dark:text-blue-300">
                Export your data first
              </h4>
              <p class="text-sm text-blue-700 dark:text-blue-400 mt-1">
                We recommend downloading a copy of your data before deleting your account.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                class="mt-2 border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-800/20"
                @click="exportDataBeforeDeletion"
              >
                Export My Data
              </Button>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <Alert v-if="hasError" type="error" class="mb-4" @dismiss="clearMessages">
          {{ error }}
        </Alert>

        <!-- Deletion Button -->
        <div class="flex justify-end pt-4 border-t border-red-200 dark:border-red-700">
          <Button
            type="submit"
            variant="danger"
            :loading="isLoading"
            :disabled="!isFormValid"
            class="w-full sm:w-auto"
          >
            Permanently Delete Account
          </Button>
        </div>
      </form>
    </div>
  </Card>

  <!-- Confirmation Dialog -->
  <Dialog
    :open="showConfirmDialog"
    @close="showConfirmDialog = false"
    title="Confirm Account Deletion"
    size="lg"
  >
    <div class="space-y-4">
      <div class="flex items-center space-x-3">
        <ExclamationTriangleIcon class="w-8 h-8 text-red-500" />
        <div>
          <h3 class="text-lg font-medium text-gray-900 dark:text-white">
            Are you absolutely sure?
          </h3>
          <p class="text-sm text-gray-600 dark:text-gray-300">
            This action cannot be undone and will permanently delete your account.
          </p>
        </div>
      </div>

      <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
        <p class="text-sm text-gray-700 dark:text-gray-200">
          <strong>Account:</strong> {{ currentUser?.email || 'Not available' }}
        </p>
        <p class="text-sm text-gray-700 dark:text-gray-200">
          <strong>Account ID:</strong> {{ currentUser?.id || 'Not available' }}
        </p>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end space-x-3">
        <Button variant="outline" @click="showConfirmDialog = false" :disabled="isLoading">
          Cancel
        </Button>
        <Button variant="danger" @click="confirmDeletion" :loading="isLoading">
          Yes, Delete Account
        </Button>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/vue/24/outline';
  import { Card, Button, Input, Alert, Dialog } from '@asabdev/asab-ui';
  import { useUserProfile } from '../composables/useUserProfile';
  import { useAuth } from '@/features/auth/composables/useAuth';
  import { accountDeletionSchema } from '@/lib/validation/users';

  const { logout } = useAuth();

  const emit = defineEmits<{
    'account-deleted': [];
    'export-data': [];
  }>();

  const { currentUser, isLoading, hasError, error, clearMessages, deleteAccount } =
    useUserProfile();

  // Form state
  const form = ref({
    confirmText: '',
    password: '',
  });

  const confirmTextError = ref<string>('');
  const passwordError = ref<string>('');
  const showConfirmDialog = ref(false);

  // Form validation
  const isFormValid = computed(() => {
    try {
      accountDeletionSchema.parse(form.value);
      return !confirmTextError.value && !passwordError.value;
    } catch {
      return false;
    }
  });

  // Validation functions
  const validateForm = () => {
    try {
      accountDeletionSchema.parse(form.value);
      confirmTextError.value = '';
      passwordError.value = '';
    } catch (error: unknown) {
      const zodError = error as { errors?: Array<{ path: string[]; message: string }> };
      const errors = zodError.errors || [];

      confirmTextError.value = '';
      passwordError.value = '';

      errors.forEach(err => {
        if (err.path.includes('confirmText')) {
          confirmTextError.value = err.message;
        } else if (err.path.includes('password')) {
          passwordError.value = err.message;
        }
      });
    }
  };

  // Watch form for real-time validation
  watch(form, validateForm, { deep: true });

  // Methods
  const exportDataBeforeDeletion = async () => {
    emit('export-data');
  };

  const handleDeleteAccount = () => {
    validateForm();
    if (!isFormValid.value) return;

    showConfirmDialog.value = true;
  };

  const confirmDeletion = async () => {
    try {
      // Call the real account deletion API with password verification
      await deleteAccount(form.value.password);

      // Clear local session data
      await logout();

      // Emit account deleted event
      emit('account-deleted');

      showConfirmDialog.value = false;
    } catch (error) {
      console.error('Account deletion failed:', error);
      showConfirmDialog.value = false;
      // Error will be shown in the component via the error state from useUserProfile
    }
  };
</script>
